import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BUSINESS_INFO } from "../config";

export function OpeningOfferModal() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    // Tjek om brugeren allerede har lukket pop-up'en i denne browser-session
    const hasSeenOffer = sessionStorage.getItem("opening_offer_dismissed");
    if (!hasSeenOffer) {
      // Vis pop-up efter et kort delay for en blød og behagelig oplevelse
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        handleClose();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    // Undgå at baggrunden scroller mens pop-up er åben
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    try {
      sessionStorage.setItem("opening_offer_dismissed", "true");
    } catch {
      // Ignore if localStorage/sessionStorage is blocked
    }
  };

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="opening-offer-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 bg-black/60 backdrop-blur-xs animate-modal-fade-in transition-opacity"
      onClick={handleClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl border border-[#E2D2C3] animate-modal-scale-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Dekorativ top-accent */}
        <div className="h-3 w-full bg-gradient-to-r from-[#dabeb9] via-[#E2D2C3] to-[#dabeb9]" />

        {/* Luk-knap */}
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-5 right-5 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-neutral-100 text-neutral-600 transition-colors hover:bg-neutral-200 hover:text-black focus:outline-hidden focus:ring-2 focus:ring-[#dabeb9]"
          aria-label="Luk tilbudsvindue"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>

        <div className="p-6 sm:p-8 text-center">
          {/* Ikon & Rabatmærke */}
          <div className="mx-auto mb-4 flex items-center justify-center">
            <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#dabeb9]/40 text-black shadow-inner">
              <i className="fa-solid fa-scissors text-2xl text-black" aria-hidden="true" />
              <span className="absolute -bottom-1 -right-1 flex items-center justify-center rounded-full bg-black text-white text-[11px] font-bold px-2 py-0.5 shadow-sm">
                -20%
              </span>
            </div>
          </div>

          {/* Badge */}
          <div className="inline-flex items-center gap-2 rounded-full bg-[#dabeb9]/40 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-black mb-3">
            <i className="fa-solid fa-gift text-black" aria-hidden="true" />
            <span>Stort åbningstilbud</span>
          </div>

          {/* Overskrift */}
          <h2
            id="opening-offer-title"
            className="font-heading text-2xl sm:text-3xl font-medium tracking-tight text-black leading-snug"
          >
            Spar 20% på behandlinger
          </h2>

          {/* Tekst */}
          <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-md mx-auto">
            Trine er nu færdiguddannet hundefrisør! Det fejrer vi i salonen med et særligt åbningstilbud, hvor du får <strong>20% rabat</strong> på behandlinger.
          </p>

          {/* Fordele / Trygheds-punkter */}
          <div className="mt-5 rounded-2xl bg-[#fbf8f5] border border-[#E2D2C3]/60 p-3.5 text-xs text-neutral-700 grid grid-cols-2 gap-2 text-left">
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check text-green-700 text-sm" aria-hidden="true" />
              <span>Uddannet hundefrisør</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check text-green-700 text-sm" aria-hidden="true" />
              <span>Rolige, trygge rammer</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check text-green-700 text-sm" aria-hidden="true" />
              <span>God tid til hver hund</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="fa-solid fa-check text-green-700 text-sm" aria-hidden="true" />
              <span>Skånsomme plejeprodukter</span>
            </div>
          </div>

          {/* Handlingsknapper */}
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
            <a
              href={BUSINESS_INFO.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClose}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#dabeb9] px-6 py-3.5 text-sm font-semibold tracking-wide text-black shadow-sm transition-all hover:bg-[#c9a7a0] active:scale-98"
            >
              <i className="fa-solid fa-calendar-check text-black" aria-hidden="true" />
              <span>Book tid & spar 20%</span>
            </a>

            <Link
              to="/ydelser-priser"
              onClick={handleClose}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-1.5 rounded-full border border-neutral-300 bg-white px-5 py-3 text-sm font-medium text-neutral-800 transition-colors hover:bg-neutral-50 hover:text-black hover:border-neutral-400"
            >
              <span>Se behandlinger</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          {/* Lille footer bemærkning */}
          <p className="mt-4 text-[11px] text-neutral-600">
            Rabatten gælder tidsbestillinger i åbningsperioden. Brovadvej 56, 7000 Fredericia.{" "}
            <Link to="/kontakt" onClick={handleClose} className="underline hover:text-black font-medium">
              Har du spørgsmål? Kontakt os
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
